<script setup lang="ts">
/**
 * Body of /how-to-prepare-for-an-interview.
 *
 * Same order as the resume guide: the whole answer in one paragraph first (the
 * passage an AI answer engine can lift), then what an interview tests, then the
 * procedure, then the reference material people search for separately (STAR,
 * the question bank, questions to ask, the day, the thank-you email).
 *
 * The product shows up where it is relevant: tailoring the CV sits inside the
 * "re-read your CV" step, the online CV inside the video checklist, and the
 * builder at the end for readers who are not getting invited yet.
 *
 * Sample answers reuse Alexandra Novak from the resume guide on purpose, so
 * the STAR result visibly traces back to a bullet on her CV.
 */
import { INTERVIEW_STEP_IDS } from '~/constants/guidePages'

const { t } = useI18n()

const steps = INTERVIEW_STEP_IDS
const starParts = ['s', 't', 'a', 'r'] as const
const questions = ['tellMe', 'whyJob', 'weakness', 'leaving', 'failure', 'conflict', 'salary', 'fiveYears'] as const
const askItems = ['success', 'challenge', 'team', 'previous', 'measure', 'next'] as const
const inPerson = ['arrive', 'bring', 'dress', 'reception'] as const
const video = ['test', 'camera', 'light', 'notes'] as const

/** In-page anchors, so long-tail readers can jump straight to their part. */
const toc = computed(() => [
  { href: '#preparation', label: t('interviewGuide.steps.eyebrow') },
  { href: '#star-method', label: t('interviewGuide.star.eyebrow') },
  { href: '#common-questions', label: t('interviewGuide.questions.eyebrow') },
  { href: '#questions-to-ask', label: t('interviewGuide.ask.title') },
  { href: '#on-the-day', label: t('interviewGuide.day.eyebrow') },
  { href: '#thank-you-email', label: t('interviewGuide.after.eyebrow') },
])

const signoff = computed(() => t('interviewGuide.after.email.signoff').split('\n'))
</script>

<template>
  <div class="sofax">
    <!-- The answer, before anything else on the page -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-sm">
        <div class="shell">
          <div class="mx-auto max-w-3xl rounded-[0.625rem] border-l-4 border-accent bg-surface p-[1.875rem] dark:bg-gray-800">
            <div class="mb-3 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
              {{ t('interviewGuide.short.label') }}
            </div>
            <p class="text-lg">{{ t('interviewGuide.short.text') }}</p>
          </div>

          <nav class="mx-auto mt-8 flex max-w-3xl flex-wrap gap-x-6 gap-y-3" :aria-label="t('resumeExamples.page.jumpLabel')">
            <a v-for="item in toc" :key="item.href" :href="item.href" class="link text-base">{{ item.label }}</a>
          </nav>
        </div>
      </div>
    </section>

    <!-- What an interview checks, before any advice -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space-bottom">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('interviewGuide.what.eyebrow')"
            :title="t('interviewGuide.what.title')"
            width="max-w-4xl"
          />
          <div class="mx-auto max-w-3xl">
            <p class="text-lg">{{ t('interviewGuide.what.p1') }}</p>
            <p class="mt-6 text-lg">{{ t('interviewGuide.what.p2') }}</p>
            <p class="mt-6 text-lg">{{ t('interviewGuide.what.p3') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- The procedure; mirrored into the HowTo graph -->
    <section id="preparation" class="surface-alt scroll-mt-24">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('interviewGuide.steps.eyebrow')"
            :title="t('interviewGuide.steps.title')"
            :text="t('interviewGuide.steps.lead')"
            width="max-w-4xl"
          />

          <!-- Last-minute version: the "24 hours" query, which redirects here -->
          <div class="mx-auto mb-6 max-w-4xl rounded-[0.625rem] border-l-4 border-accent bg-white p-[1.875rem] dark:bg-gray-800">
            <div class="mb-2 flex items-center gap-2 font-title text-lg font-bold text-ink dark:text-white">
              <i class="bi bi-alarm shrink-0 text-accent" aria-hidden="true" />
              {{ t('interviewGuide.steps.rush.title') }}
            </div>
            <p class="text-base">{{ t('interviewGuide.steps.rush.text') }}</p>
          </div>

          <ol class="mx-auto flex max-w-4xl flex-col gap-6">
            <li
              v-for="(step, index) in steps"
              :id="`step-${index + 1}`"
              :key="step"
              class="scroll-mt-24 rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800"
            >
              <div class="mb-5 flex items-start gap-5">
                <span
                  class="inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-ink !text-base !font-bold !text-white dark:bg-white dark:!text-ink"
                  aria-hidden="true"
                >{{ index + 1 }}</span>
                <div class="flex-1">
                  <h3 class="!text-xl md:!text-2xl">{{ t(`interviewGuide.steps.items.${step}.title`) }}</h3>
                  <div class="mt-2 text-sm font-semibold text-accent">{{ t(`interviewGuide.steps.items.${step}.time`) }}</div>
                </div>
              </div>

              <p class="!mb-4 text-base">{{ t(`interviewGuide.steps.items.${step}.text`) }}</p>
              <p class="text-base text-muted dark:text-gray-400">{{ t(`interviewGuide.steps.items.${step}.detail`) }}</p>

              <!-- Step 3: the interview is about the CV, so tailoring it is the tool here -->
              <div v-if="step === 'cv'" class="mt-6">
                <SofaxBtn to="/ats-check" :label="t('interviewGuide.steps.cvCta')" variant="accent" />
                <span class="mt-4 block text-sm text-muted dark:text-gray-400">{{ t('interviewGuide.steps.cvCtaNote') }}</span>
              </div>

              <!-- Step 4 hands over to the STAR section below -->
              <p v-if="step === 'stories'" class="mt-5">
                <a href="#star-method" class="link">{{ t('interviewGuide.star.title') }} →</a>
              </p>
            </li>
          </ol>
        </div>
      </div>
    </section>

    <!-- STAR, with the same question answered weakly and well -->
    <section id="star-method" class="scroll-mt-24 bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('interviewGuide.star.eyebrow')"
            :title="t('interviewGuide.star.title')"
            :text="t('interviewGuide.star.lead')"
            width="max-w-4xl"
          />

          <div class="mx-auto grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div v-for="part in starParts" :key="part" class="rounded-[0.625rem] bg-surface p-5 dark:bg-gray-800">
              <span
                class="inline-flex size-10 items-center justify-center rounded-full bg-accent !text-base !font-bold !text-white"
                aria-hidden="true"
              >{{ t(`interviewGuide.star.parts.${part}.letter`) }}</span>
              <div class="mt-4 font-title text-lg font-bold text-ink dark:text-white">{{ t(`interviewGuide.star.parts.${part}.name`) }}</div>
              <p class="mt-2 text-base">{{ t(`interviewGuide.star.parts.${part}.text`) }}</p>
            </div>
          </div>

          <div class="mx-auto mt-10 max-w-4xl">
            <p class="!mb-5 font-title text-lg font-bold text-ink dark:text-white">{{ t('interviewGuide.star.exampleQuestion') }}</p>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div class="rounded-[0.625rem] bg-surface p-5 dark:bg-gray-900">
                <div class="mb-2 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
                  <i class="bi bi-x-circle shrink-0 text-lg text-muted" />
                  {{ t('interviewGuide.star.weakLabel') }}
                </div>
                <p class="text-base">{{ t('interviewGuide.star.weak') }}</p>
              </div>
              <div class="rounded-[0.625rem] bg-accent-soft/20 p-5 dark:bg-accent/10">
                <div class="mb-2 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
                  <i class="bi bi-check-circle-fill shrink-0 text-lg text-accent" />
                  {{ t('interviewGuide.star.strongLabel') }}
                </div>
                <p v-for="part in starParts" :key="part" class="mt-2 text-base first:mt-0">
                  {{ t(`interviewGuide.star.strong.${part}`) }}
                </p>
              </div>
            </div>
            <p class="mt-5 text-base text-muted dark:text-gray-400">{{ t('interviewGuide.star.note') }}</p>
            <p class="mt-4">
              <LocaleLink to="/how-to-write-a-resume" class="link">{{ t('interviewGuide.star.cvLink') }} →</LocaleLink>
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- The question bank: each question is its own h3, so each can rank on its own -->
    <section id="common-questions" class="surface-alt scroll-mt-24">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('interviewGuide.questions.eyebrow')"
            :title="t('interviewGuide.questions.title')"
            :text="t('interviewGuide.questions.lead')"
            width="max-w-4xl"
          />

          <ol class="mx-auto flex max-w-4xl flex-col gap-6">
            <li
              v-for="(q, index) in questions"
              :id="`question-${index + 1}`"
              :key="q"
              class="scroll-mt-24 rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800"
            >
              <h3 class="!text-xl md:!text-2xl">{{ index + 1 }}. {{ t(`interviewGuide.questions.items.${q}.q`) }}</h3>

              <dl class="mt-5 flex flex-col gap-4">
                <div>
                  <dt class="text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">{{ t('interviewGuide.questions.reallyLabel') }}</dt>
                  <dd class="mt-1 text-base">{{ t(`interviewGuide.questions.items.${q}.really`) }}</dd>
                </div>
                <div>
                  <dt class="text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">{{ t('interviewGuide.questions.howLabel') }}</dt>
                  <dd class="mt-1 text-base">{{ t(`interviewGuide.questions.items.${q}.how`) }}</dd>
                </div>
              </dl>

              <div class="mt-5 rounded-[0.625rem] bg-accent-soft/20 p-5 dark:bg-accent/10">
                <div class="mb-2 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
                  <i class="bi bi-chat-quote-fill shrink-0 text-lg text-accent" />
                  {{ t('interviewGuide.questions.sampleLabel') }}
                </div>
                <p class="text-base">{{ t(`interviewGuide.questions.items.${q}.sample`) }}</p>
              </div>
            </li>
          </ol>

          <p class="mx-auto mt-6 max-w-4xl text-sm text-muted dark:text-gray-400">{{ t('interviewGuide.questions.disclaimer') }}</p>
        </div>
      </div>
    </section>

    <!-- Questions to ask them -->
    <section id="questions-to-ask" class="scroll-mt-24 bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('interviewGuide.ask.eyebrow')"
            :title="t('interviewGuide.ask.title')"
            :text="t('interviewGuide.ask.lead')"
            width="max-w-4xl"
          />

          <ul class="mx-auto flex max-w-3xl flex-col gap-4">
            <li v-for="item in askItems" :key="item" class="flex items-start gap-4">
              <SofaxIconTile icon="bi-question-lg" round size="sm" />
              <p class="pt-2 text-base">{{ t(`interviewGuide.ask.items.${item}`) }}</p>
            </li>
          </ul>

          <div class="mx-auto mt-8 max-w-3xl rounded-[0.625rem] bg-surface p-5 dark:bg-gray-800">
            <div class="mb-2 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
              <i class="bi bi-x-circle shrink-0 text-lg text-muted" />
              {{ t('interviewGuide.ask.avoidTitle') }}
            </div>
            <p class="text-base">{{ t('interviewGuide.ask.avoid') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- On the day -->
    <section id="on-the-day" class="surface-alt scroll-mt-24">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('interviewGuide.day.eyebrow')"
            :title="t('interviewGuide.day.title')"
            width="max-w-4xl"
          />

          <div class="mx-auto grid max-w-4xl grid-cols-1 gap-6 md:grid-cols-2">
            <div class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800">
              <h3 class="!mb-5 flex items-center gap-3 !text-xl">
                <i class="bi bi-building shrink-0 text-accent" aria-hidden="true" />
                {{ t('interviewGuide.day.inPersonTitle') }}
              </h3>
              <ul class="flex flex-col gap-3">
                <li v-for="item in inPerson" :key="item" class="flex items-start gap-3 text-base">
                  <i class="bi bi-check-lg mt-0.5 shrink-0 text-lg text-accent" aria-hidden="true" />
                  <span>{{ t(`interviewGuide.day.inPerson.${item}`) }}</span>
                </li>
              </ul>
            </div>
            <div class="rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800">
              <h3 class="!mb-5 flex items-center gap-3 !text-xl">
                <i class="bi bi-camera-video shrink-0 text-accent" aria-hidden="true" />
                {{ t('interviewGuide.day.videoTitle') }}
              </h3>
              <ul class="flex flex-col gap-3">
                <li v-for="item in video" :key="item" class="flex items-start gap-3 text-base">
                  <i class="bi bi-check-lg mt-0.5 shrink-0 text-lg text-accent" aria-hidden="true" />
                  <span>{{ t(`interviewGuide.day.video.${item}`) }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="mx-auto mt-6 flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p class="!text-base text-muted dark:text-gray-400">{{ t('interviewGuide.day.linkTip') }}</p>
            <SofaxArrowLink to="/online-cv" :label="t('interviewGuide.day.linkCta')" class="shrink-0" />
          </div>
        </div>
      </div>
    </section>

    <!-- After: the email, written out so it can be copied -->
    <section id="thank-you-email" class="scroll-mt-24 bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('interviewGuide.after.eyebrow')"
            :title="t('interviewGuide.after.title')"
            :text="t('interviewGuide.after.lead')"
            width="max-w-4xl"
          />

          <article class="mx-auto max-w-3xl rounded-[0.625rem] border border-line p-8 sm:p-10 dark:border-gray-700">
            <div class="mb-5 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
              {{ t('interviewGuide.after.emailLabel') }}
            </div>
            <p class="font-semibold text-ink dark:text-white">{{ t('interviewGuide.after.email.subject') }}</p>
            <p class="mt-5 text-base">{{ t('interviewGuide.after.email.greeting') }}</p>
            <p class="mt-4 text-base">{{ t('interviewGuide.after.email.p1') }}</p>
            <p class="mt-4 text-base">{{ t('interviewGuide.after.email.p2') }}</p>
            <p class="mt-4 text-base">{{ t('interviewGuide.after.email.p3') }}</p>
            <p class="mt-4 text-base">
              <template v-for="(line, i) in signoff" :key="i">
                <br v-if="i > 0">{{ line }}
              </template>
            </p>
          </article>

          <div class="mx-auto mt-8 max-w-3xl">
            <h3 class="!text-xl">{{ t('interviewGuide.after.followUpTitle') }}</h3>
            <p class="mt-3 text-base">{{ t('interviewGuide.after.followUp') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- For readers who are not getting invited: the step before the interview -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <div class="mx-auto max-w-3xl text-center">
            <div class="eyebrow mb-5">{{ t('interviewGuide.invite.eyebrow') }}</div>
            <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('interviewGuide.invite.title') }}</h2>
            <p class="mt-5 text-lg">{{ t('interviewGuide.invite.p1') }}</p>
            <p class="mt-5 text-lg">{{ t('interviewGuide.invite.p2') }}</p>
            <div class="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <SofaxBtn to="/free-cv-builder" :label="t('interviewGuide.invite.cta')" variant="accent" />
              <SofaxBtn to="/ats-check" :label="t('interviewGuide.invite.secondary')" variant="dark" />
            </div>
            <p class="mt-6">
              <LocaleLink to="/how-to-write-a-resume" class="link">{{ t('interviewGuide.invite.writeLink') }} →</LocaleLink>
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
